// src/quality-management/components/WeeklyCalendarView.tsx
// Refactored with Precision with Warmth design system

import React, { useState, useEffect } from 'react';
import { Project, TimeEntry, Task, TaskStatus } from '../services/api/types';
import { useApi } from '../hooks/useApi';
import { apiService } from '../services/api';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';
import { Avatar, Badge, Spinner, EmptyState } from '../../shared/components/ui';
import { getEventColor } from '../../shared/lib/eventColors';
import './WeeklyCalendarView.css';

interface WeeklyCalendarViewProps {
  project: Project;
  weekStart: Date;
  // Quando false, omite o cabeçalho com nome/descrição (o pai já o exibe)
  showHeader?: boolean;
}

// Employee info type used in this component
interface EmployeeInfo {
  id: number;
  name: string;
  username?: string;
  role?: string;
}

const WeeklyCalendarView: React.FC<WeeklyCalendarViewProps> = ({ project, weekStart, showHeader = true }) => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [employees, setEmployees] = useState<EmployeeInfo[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingProgress, setEditingProgress] = useState<number | null>(null);
  const [progressInput, setProgressInput] = useState<string>('');
  const [employeesLoading, setEmployeesLoading] = useState(true);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });

  const { data: weeklyEntries, loading: entriesLoading, execute: fetchTimeEntries } = useApi(
    () => {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return apiService.timeEntries.getWeeklyTimeEntries(
        weekStart.toISOString().split('T')[0],
        weekEnd.toISOString().split('T')[0],
        project.id
      );
    },
    [project.id, weekStart],
    { immediate: false }
  );

  // Fetch employee names using the dedicated endpoint
  useEffect(() => {
    const fetchEmployeeNames = async () => {
      setEmployeesLoading(true);
      try {
        const employeeNamesMap = await apiService.users.getEmployeeNames();
        // Convert map to array of EmployeeInfo for employees assigned to this project
        const assignedIds = project.assigned_employee_ids || [];
        const employeeList: EmployeeInfo[] = assignedIds.map(empId => {
          const info = employeeNamesMap[empId];
          return {
            id: empId,
            name: info?.name || `Employee ${empId}`,
            username: info?.username,
            role: info?.role
          };
        });
        setEmployees(employeeList);
      } catch (error) {
        console.error('Error fetching employee names:', error);
        // Fallback to using IDs
        const assignedIds = project.assigned_employee_ids || [];
        setEmployees(assignedIds.map(empId => ({
          id: empId,
          name: `Employee ${empId}`
        })));
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployeeNames();
  }, [project.assigned_employee_ids]);

  const { data: projectTasks, loading: tasksLoading, execute: refetchTasks } = useApi(
    () => apiService.tasks.getTasksForProject(project.id),
    [project.id]
  );

  useEffect(() => {
    fetchTimeEntries();
  }, [project.id, weekStart]);

  useEffect(() => {
    if (weeklyEntries && Array.isArray(weeklyEntries)) {
      setTimeEntries(weeklyEntries);
    }
  }, [weeklyEntries]);


  useEffect(() => {
    if (projectTasks && Array.isArray(projectTasks)) {
      setTasks(projectTasks);
    }
  }, [projectTasks]);

  const getHoursForEmployeeAndDay = (employeeId: number, date: Date): TimeEntry[] => {
    const dateString = date.toISOString().split('T')[0];
    return timeEntries.filter(entry =>
      entry.employee_id === employeeId && entry.date === dateString
    );
  };

  const getTasksForEmployeeAndDay = (employeeId: number, date: Date): Task[] => {
    const dateString = date.toISOString().split('T')[0];
    return tasks.filter(task => {
      const isAssignedToEmployee = task.assigned_employee_ids && Array.isArray(task.assigned_employee_ids) && task.assigned_employee_ids.includes(employeeId);
      // Extract just the date part from task_date (handles both ISO strings and date-only strings)
      const taskDateStr = task.task_date ? task.task_date.split('T')[0] : null;
      const isTaskDateMatch = taskDateStr === dateString;
      return isAssignedToEmployee && isTaskDateMatch;
    });
  };

  const getTotalHoursForDay = (date: Date): number => {
    const dateString = date.toISOString().split('T')[0];
    const timeEntryHours = timeEntries
      .filter(entry => entry.date === dateString)
      .reduce((total, entry) => total + entry.hours, 0);

    const taskHours = assignedEmployees.reduce((total, employee) => {
      const dayTasks = getTasksForEmployeeAndDay(employee.id, date);
      return total + dayTasks.reduce((taskTotal, task) => taskTotal + (task.expected_hours || 0), 0);
    }, 0);

    return timeEntryHours + taskHours;
  };

  const getTotalHoursForEmployee = (employeeId: number): number => {
    const timeEntryHours = timeEntries
      .filter(entry => entry.employee_id === employeeId)
      .reduce((total, entry) => total + entry.hours, 0);

    const taskHours = tasks
      .filter(task => task.assigned_employee_ids && Array.isArray(task.assigned_employee_ids) && task.assigned_employee_ids.includes(employeeId))
      .reduce((total, task) => {
        const regularHours = task.expected_hours || 0;
        let overtimeHours = 0;
        if (task.has_overtime && task.overtime_hours && task.overtime_approval_status === 'approved') {
          overtimeHours = task.overtime_hours;
        }
        return total + regularHours + overtimeHours;
      }, 0);

    return timeEntryHours + taskHours;
  };

  const getTaskName = (taskId: number): string => {
    const task = tasks.find(t => t.id === taskId);
    return task?.name || `Task ${taskId}`;
  };

  const getProjectColor = (projectId: number): string => getEventColor(projectId);

  // employees is already filtered to assigned employees from the useEffect above
  const assignedEmployees = employees;

  // Guarda o funcionário da linha clicada para pré-selecionar no modal
  const [taskEmployeeId, setTaskEmployeeId] = useState<number | null>(null);

  const handleAddTaskClick = (date: Date, employeeId: number | null, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedDate(date);
    setTaskEmployeeId(employeeId);
    setShowCreateTaskModal(true);
  };

  const handleTaskClick = (task: Task, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTask(task);
    setShowEditTaskModal(true);
  };

  const handleTaskCreated = () => {
    refetchTasks();
    fetchTimeEntries();
  };

  const handleTaskUpdated = () => {
    refetchTasks();
    fetchTimeEntries();
  };

  const handleCloseCreateModal = () => {
    setShowCreateTaskModal(false);
    setSelectedDate(null);
    setTaskEmployeeId(null);
  };

  const handleCloseEditModal = () => {
    setShowEditTaskModal(false);
    setSelectedTask(null);
  };

  const handleProgressClick = (task: Task, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingProgress(task.id);
    setProgressInput(String(task.completion_percentage || 0));
  };

  const handleProgressSave = async (task: Task) => {
    const newProgress = Math.min(Math.max(parseInt(progressInput) || 0, 0), 100);

    try {
      const updateData: any = { completion_percentage: newProgress };
      let newStatus: TaskStatus = task.status;

      // Auto-update status based on progress
      if (newProgress >= 100 && task.status !== 'completed') {
        newStatus = 'completed';
        updateData.status = newStatus;
      } else if (newProgress < 100 && task.status === 'completed') {
        newStatus = 'in_progress';
        updateData.status = newStatus;
      }

      await apiService.tasks.updateTask(task.id, updateData);
      refetchTasks();
      fetchTimeEntries();
    } catch (error) {
      console.error('Error updating task progress:', error);
    } finally {
      setEditingProgress(null);
      setProgressInput('');
    }
  };

  const handleProgressCancel = () => {
    setEditingProgress(null);
    setProgressInput('');
  };

  const handleProgressKeyPress = (event: React.KeyboardEvent, task: Task) => {
    if (event.key === 'Enter') {
      handleProgressSave(task);
    } else if (event.key === 'Escape') {
      handleProgressCancel();
    }
  };

  const loading = entriesLoading || employeesLoading || tasksLoading;

  const statusMeta = (status: string): { label: string; tone: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' } => {
    switch (status) {
      case 'pending': return { label: 'Pendente', tone: 'neutral' };
      case 'in_progress': return { label: 'Em Andamento', tone: 'info' };
      case 'completed': return { label: 'Concluído', tone: 'success' };
      case 'cancelled': return { label: 'Cancelado', tone: 'danger' };
      case 'pending_overtime_approval': return { label: 'Aguardando Aprovação', tone: 'warning' };
      default: return { label: status, tone: 'neutral' };
    }
  };

  if (loading) {
    return (
      <div className="weekly-calendar flex flex-col items-center justify-center gap-4 py-16 text-foreground-muted">
        <Spinner size="lg" />
        <span className="text-sm">Carregando dados da semana...</span>
      </div>
    );
  }

  const colTemplate = { gridTemplateColumns: 'minmax(200px, 1.4fr) repeat(7, minmax(132px, 1fr)) minmax(104px, 0.9fr)' };

  return (
    <div className="weekly-calendar">
      {showHeader && (
        <div className="mb-5">
          <h3 className="font-display text-xl font-semibold text-foreground">
            {project.name}
          </h3>
          {project.description && (
            <p className="mt-1 text-[15px] text-foreground-muted">{project.description}</p>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-surface-1 shadow-sm">
        <div className="grid min-w-[920px]" style={colTemplate}>
          {/* Header Row */}
          <div className="flex items-center gap-2 border-b border-border bg-surface-2 px-4 py-3 text-sm font-semibold text-foreground-muted">
            Equipe ({assignedEmployees.length})
          </div>
          {weekDays.map(day => (
            <div
              key={day.toISOString()}
              className="flex flex-col items-center gap-0.5 border-b border-l border-border bg-surface-2 px-2 py-3"
            >
              <div className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
                {day.toLocaleDateString('pt-BR', { weekday: 'short' })}
              </div>
              <div className="font-display text-lg font-semibold text-foreground tabular-nums">{day.getDate()}</div>
              <div className="text-xs font-medium text-brand tabular-nums">{Math.round(getTotalHoursForDay(day))}h</div>
            </div>
          ))}
          <div className="flex items-center justify-center border-b border-l border-border bg-surface-2 px-3 py-3 text-center text-sm font-semibold text-foreground-muted">
            Total Semanal
          </div>

          {/* Employee Rows */}
          {assignedEmployees.map(employee => {
            const employeeId = employee.id;
            const isSelected = selectedEmployee === employee.id;

            return (
              <React.Fragment key={employee.id}>
                <button
                  type="button"
                  className={`flex items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-surface-2 ${
                    isSelected ? 'bg-brand/5 ring-1 ring-inset ring-brand/20' : ''
                  }`}
                  onClick={() => setSelectedEmployee(isSelected ? null : employee.id)}
                >
                  <Avatar name={employee.name} size="sm" />
                  <span className="font-medium text-foreground">{employee.name}</span>
                </button>

                {weekDays.map(day => {
                  const dayEntries = getHoursForEmployeeAndDay(employeeId, day);
                  const dayTasks = getTasksForEmployeeAndDay(employeeId, day);
                  const timeEntryHours = dayEntries.reduce((sum, entry) => sum + entry.hours, 0);
                  const taskHours = dayTasks.reduce((sum, task) => sum + (task.expected_hours || 0), 0);
                  const totalHours = timeEntryHours + taskHours;

                  return (
                    <div
                      key={`${employee.id}-${day.toISOString()}`}
                      className="group relative min-h-[88px] cursor-pointer border-b border-l border-border p-2 transition-colors hover:bg-surface-2"
                      onClick={(e) => handleAddTaskClick(day, employee.id, e)}
                      title="Clique para adicionar tarefa"
                      data-testid="calendar-day"
                    >
                      {(dayEntries.length > 0 || dayTasks.length > 0) ? (
                        <div className="flex flex-col gap-1.5">
                          <div className="text-xs font-semibold text-foreground-muted tabular-nums">
                            {Math.round(totalHours)}h
                          </div>

                          {dayEntries.map(entry => (
                            <div
                              key={`entry-${entry.id}`}
                              className="rounded-lg border-l-[3px] bg-surface-3 px-2 py-1.5 shadow-xs"
                              style={{ borderLeftColor: getProjectColor(entry.project_id) }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-[13px] font-medium text-foreground">
                                  {getTaskName(entry.task_id)}
                                </span>
                                <span className="text-[13px] font-semibold text-foreground tabular-nums">{entry.hours}h</span>
                              </div>
                              {entry.description && (
                                <div className="mt-0.5 truncate text-[12px] text-foreground-subtle" title={entry.description}>
                                  {entry.description.length > 30
                                    ? `${entry.description.substring(0, 30)}...`
                                    : entry.description}
                                </div>
                              )}
                            </div>
                          ))}

                          {dayTasks.map(task => {
                            const meta = statusMeta(task.status);
                            return (
                              <div
                                key={`task-${task.id}`}
                                className="cursor-pointer rounded-lg border-l-[3px] border-l-brand bg-brand/5 px-2 py-1.5 shadow-xs transition-colors hover:bg-brand/10"
                                onClick={(e) => handleTaskClick(task, e)}
                                title="Clique para editar"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <span className="truncate text-[13px] font-medium text-foreground tabular-nums">
                                    {task.name || task.title}: {(task.expected_hours || 0).toFixed(0)}h
                                  </span>
                                  <Badge tone={meta.tone} size="sm" className="shrink-0">{meta.label}</Badge>
                                </div>
                                {task.description && (
                                  <div className="mt-0.5 truncate text-[12px] text-foreground-subtle" title={task.description}>
                                    {task.description.length > 30
                                      ? `${task.description.substring(0, 30)}...`
                                      : task.description}
                                  </div>
                                )}
                                <div className="mt-1.5">
                                  {editingProgress === task.id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={progressInput}
                                        onChange={(e) => setProgressInput(e.target.value)}
                                        onKeyPress={(e) => handleProgressKeyPress(e, task)}
                                        onBlur={() => handleProgressSave(task)}
                                        className="h-7 w-14 rounded-md border border-border bg-surface-1 px-1.5 text-[13px] text-foreground tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                                        autoFocus
                                      />
                                      <span className="text-[12px] text-foreground-muted">%</span>
                                    </div>
                                  ) : (
                                    <div
                                      className="relative h-4 cursor-pointer overflow-hidden rounded-full bg-surface-4"
                                      onClick={(e) => handleProgressClick(task, e)}
                                      title={`Progresso: ${task.completion_percentage || 0}% - Clique para editar`}
                                    >
                                      <div
                                        className="h-full rounded-full bg-brand transition-all"
                                        style={{ width: `${Math.min(task.completion_percentage || 0, 100)}%` }}
                                      />
                                      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-foreground tabular-nums">
                                        {task.completion_percentage || 0}%
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center text-base text-foreground-subtle/40" aria-label="Sem registro">
                          —
                        </div>
                      )}

                      <div
                        className="pointer-events-none absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-brand text-brand-fg opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
                        title="Adicionar tarefa"
                      >
                        +
                      </div>
                    </div>
                  );
                })}

                <div className="flex flex-col items-center justify-center border-b border-l border-border px-3 py-3">
                  <div className="font-display text-lg font-semibold text-foreground tabular-nums">
                    {Math.round(getTotalHoursForEmployee(employeeId))}h
                  </div>
                  <div className="text-xs text-foreground-subtle">semanal</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {assignedEmployees.length === 0 && (
        <EmptyState
          title="Nenhum funcionário atribuído"
          description="Nenhum membro da equipe está atribuído a este projeto. Atribua funcionários ao projeto para ver o registro de horas aqui."
        />
      )}

      {showCreateTaskModal && (
        <CreateTaskModal
          projectId={project.id}
          selectedDate={selectedDate}
          assignedEmployeeIds={taskEmployeeId != null ? [taskEmployeeId] : project.assigned_employee_ids}
          onClose={handleCloseCreateModal}
          onTaskCreated={handleTaskCreated}
          availableEmployees={assignedEmployees}
        />
      )}

      {showEditTaskModal && selectedTask && (
        <EditTaskModal
          task={selectedTask}
          onClose={handleCloseEditModal}
          onTaskUpdated={handleTaskUpdated}
        />
      )}
    </div>
  );
};

export default WeeklyCalendarView;
